import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Paper,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Alert,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  useTheme,
  useMediaQuery
} from '@mui/material';
import { DataGrid, GridColDef, GridActionsCellItem } from '@mui/x-data-grid';
import { Edit, Delete, Add } from '@mui/icons-material';
import { workCenterAnnualBagsApi, WorkCenterAnnualBag, WorkCenterAnnualBagCreate, WorkCenterAnnualBagUpdate } from '../services/workCenterAnnualBagsApi';
import { getWorkCenters } from '../services/api';
import { WorkCenter } from '../types';

const WorkCenterAnnualBags: React.FC = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const [bags, setBags] = useState<WorkCenterAnnualBag[]>([]);
  const [workCenters, setWorkCenters] = useState<WorkCenter[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [openDialog, setOpenDialog] = useState(false);
  const [editingBag, setEditingBag] = useState<WorkCenterAnnualBag | null>(null);
  const [formData, setFormData] = useState<WorkCenterAnnualBagCreate>({
    work_center_id: 0,
    year: new Date().getFullYear(),
    vacation_days: 22,
    flexible_hours: 58,
    holidays_count: 10,
    notes: ''
  });

  const columns: GridColDef[] = [
    {
      field: 'work_center_name',
      headerName: 'Centro de Trabajo',
      flex: 2,
      minWidth: 140,
    },
    {
      field: 'year',
      headerName: 'Año',
      flex: 1,
      minWidth: 70,
      align: 'left',
      headerAlign: 'left',
    },
    {
      field: 'vacation_days',
      headerName: 'Días Vacaciones',
      flex: 1,
      minWidth: 120,
      align: 'left',
      headerAlign: 'left',
    },
    {
      field: 'flexible_hours',
      headerName: 'Horas Flexibles',
      flex: 1,
      minWidth: 110,
      align: 'left',
      headerAlign: 'left',
      renderCell: (params: any) => `${params.value}h`,
    },
    {
      field: 'holidays_count',
      headerName: 'Festivos',
      flex: 1,
      minWidth: 70,
      align: 'left',
      headerAlign: 'left',
    },
    {
      field: 'notes',
      headerName: 'Notas',
      flex: 2,
      minWidth: 120,
      renderCell: (params: any) => params.value || '-',
    },
    {
      field: 'actions',
      type: 'actions',
      headerName: 'Acciones',
      width: isMobile ? 80 : 120,
      getActions: (params: any) => [
        <GridActionsCellItem
          icon={<Edit sx={{ fontSize: 18, color: '#666666' }} />}
          label="Editar"
          onClick={() => handleOpenDialog(params.row)}
        />,
        <GridActionsCellItem
          icon={<Delete sx={{ fontSize: 18, color: '#666666' }} />}
          label="Eliminar"
          onClick={() => handleDelete(params.row)}
        />,
      ],
    },
  ];

  useEffect(() => {
    loadBags();
    loadWorkCenters();
  }, []);

  const loadBags = async () => {
    try {
      setLoading(true);
      const data = await workCenterAnnualBagsApi.getAll();
      setBags(data);
      setError(null);
    } catch (err) {
      console.error('Error loading annual bags:', err);
      setError('Error al cargar las bolsas anuales');
    } finally {
      setLoading(false);
    }
  };

  const loadWorkCenters = async () => {
    try {
      const data = await getWorkCenters();
      setWorkCenters(data);
    } catch (err) {
      console.error('Error loading work centers:', err);
    }
  };

  const handleOpenDialog = (bag?: WorkCenterAnnualBag) => {
    if (bag) {
      setEditingBag(bag);
      setFormData({
        work_center_id: bag.work_center_id,
        year: bag.year,
        vacation_days: bag.vacation_days,
        flexible_hours: bag.flexible_hours,
        holidays_count: bag.holidays_count,
        notes: bag.notes || ''
      });
    } else {
      setEditingBag(null);
      setFormData({
        work_center_id: 0,
        year: new Date().getFullYear(),
        vacation_days: 22,
        flexible_hours: 58,
        holidays_count: 10,
        notes: ''
      });
    }
    setOpenDialog(true);
  };

  const handleCloseDialog = () => {
    setOpenDialog(false);
    setEditingBag(null);
  };

  const handleSubmit = async () => {
    try {
      if (editingBag) {
        const updateData: WorkCenterAnnualBagUpdate = {
          vacation_days: formData.vacation_days,
          flexible_hours: formData.flexible_hours,
          holidays_count: formData.holidays_count,
          notes: formData.notes
        };
        await workCenterAnnualBagsApi.update(editingBag.id, updateData);
      } else {
        await workCenterAnnualBagsApi.create(formData);
      }
      
      handleCloseDialog();
      loadBags();
    } catch (err: any) {
      console.error('Error saving annual bag:', err);
      setError(err.response?.data?.detail || 'Error al guardar la bolsa anual');
    }
  };

  const handleDelete = async (bag: WorkCenterAnnualBag) => {
    if (window.confirm(`¿Está seguro de eliminar la bolsa anual de ${bag.work_center_name} para ${bag.year}?`)) {
      try {
        await workCenterAnnualBagsApi.delete(bag.id);
        loadBags();
      } catch (err) {
        console.error('Error deleting annual bag:', err);
        setError('Error al eliminar la bolsa anual');
      }
    }
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" height={200}>
        <Typography>Cargando bolsas anuales...</Typography>
      </Box>
    );
  }

  return (
    <Box sx={{ p: 0 }}>
      <Box display="flex" justifyContent="space-between" alignItems={{ xs: 'stretch', sm: 'start' }} flexDirection={{ xs: 'column', sm: 'row' }} gap={{ xs: 2, sm: 0 }} mb={{ xs: 3, sm: 6 }}>
        <Box>
          <Typography 
            variant="h5" 
            sx={{ 
              mb: 1,
              fontWeight: 700,
              color: '#001923',
              letterSpacing: '-0.01em',
              fontSize: { xs: '1.1rem', sm: '1.25rem' }
            }}
          >
            Bolsas Anuales
          </Typography>
          <Typography 
            variant="body1" 
            className="descripcion-cyan"
            sx={{ 
              fontWeight: 300,
              lineHeight: 1.6
            }}
          >
            Asignación de días por centro de trabajo
          </Typography>
        </Box>
        <Button
          variant="contained"
          startIcon={<Add />}
          onClick={() => handleOpenDialog()}
          sx={{
            backgroundColor: '#004254',
            color: '#FFFFFF',
            '&:hover': {
              backgroundColor: '#003040'
            },
            alignSelf: { xs: 'stretch', sm: 'auto' }
          }}
        >
          Crear Bolsa Anual
        </Button>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      {bags.length === 0 ? (
        <Paper sx={{ p: 3, textAlign: 'center' }}>
          <Typography variant="h6" color="text.secondary">
            No hay bolsas definidas para el año {new Date().getFullYear()}
          </Typography>
          <Typography variant="body2" color="text.secondary" mt={1}>
            Cree una bolsa anual para cada centro de trabajo
          </Typography>
        </Paper>
      ) : (
        <Paper
          className="annualbags-datagrid-paper"
          sx={{
            height: 600,
            width: '100%',
            backgroundColor: '#FFFFFF',
            '&.annualbags-datagrid-paper .MuiDataGrid-root': {
              border: 'none',
              backgroundColor: '#FFFFFF'
            },
            '&.annualbags-datagrid-paper .MuiDataGrid-columnHeaders': {
              backgroundColor: '#00B0BD',
              borderBottom: '2px solid #00B0BD',
              color: '#001923'
            },
            '&.annualbags-datagrid-paper .MuiDataGrid-columnHeaderTitle': {
              color: '#001923',
              fontWeight: 600
            },
            '&.annualbags-datagrid-paper .MuiDataGrid-columnHeader': {
              padding: { xs: '0 8px', sm: '0 16px' }
            },
            '&.annualbags-datagrid-paper .MuiDataGrid-columnHeader .MuiDataGrid-sortIcon': {
              display: { xs: 'none !important', sm: 'inline-flex' }
            },
            '&.annualbags-datagrid-paper .MuiDataGrid-columnHeader .MuiDataGrid-iconButtonContainer': {
              display: { xs: 'none !important', sm: 'inline-flex' }
            },
            '&.annualbags-datagrid-paper .MuiDataGrid-cell': {
              borderBottom: '1px solid #f0f0f0',
              color: '#001923'
            },
            '@media (max-width: 600px)': {
              '&.annualbags-datagrid-paper .MuiDataGrid-actionsCell': {
                gap: '0px'
              },
              '&.annualbags-datagrid-paper .MuiDataGrid-actionsCell .MuiButtonBase-root': {
                padding: '4px',
                minWidth: 'unset'
              }
            },
            '&.annualbags-datagrid-paper .MuiDataGrid-row': {
              backgroundColor: '#FFFFFF',
              '&:hover': {
                backgroundColor: '#f5f5f5'
              }
            }
          }}
        >
          <DataGrid
            rows={bags}
            columns={columns}
            initialState={{
              pagination: {
                paginationModel: { page: 0, pageSize: 10 }
              }
            }}
            pageSizeOptions={[10, 25, 50]}
            loading={loading}
            disableRowSelectionOnClick
            disableColumnMenu={isMobile}
          />
        </Paper>
      )}

      <Dialog open={openDialog} onClose={handleCloseDialog} maxWidth="sm" fullWidth fullScreen={isMobile}>
        <DialogTitle>
          {editingBag ? 'Editar Bolsa Anual' : 'Crear Nueva Bolsa Anual'}
        </DialogTitle>
        <DialogContent>
          <Box sx={{ pt: 2, display: 'flex', flexDirection: 'column', gap: 3 }}>
            <FormControl fullWidth disabled={!!editingBag}>
              <InputLabel>Centro de Trabajo</InputLabel>
              <Select
                value={formData.work_center_id}
                onChange={(e) => setFormData({...formData, work_center_id: Number(e.target.value)})}
                label="Centro de Trabajo"
              >
                <MenuItem value={0} disabled>Seleccione un centro</MenuItem>
                {workCenters.map((center) => (
                  <MenuItem key={center.id} value={center.id}>
                    {center.name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            <TextField
              label="Año"
              type="number"
              value={formData.year}
              onChange={(e) => setFormData({...formData, year: Number(e.target.value)})}
              fullWidth
              disabled={!!editingBag}
              inputProps={{ min: 2020, max: 2030 }}
            />

            <TextField
              label="Días de Vacaciones"
              type="number"
              value={formData.vacation_days}
              onChange={(e) => setFormData({...formData, vacation_days: Number(e.target.value)})}
              fullWidth
              inputProps={{ min: 0, max: 50 }}
            />

            <TextField
              label="Horas Flexibles"
              type="number"
              value={formData.flexible_hours}
              onChange={(e) => setFormData({...formData, flexible_hours: Number(e.target.value)})}
              fullWidth
              inputProps={{ min: 0, max: 200 }}
            />

            <TextField
              label="Número de Festivos"
              type="number"
              value={formData.holidays_count}
              onChange={(e) => setFormData({...formData, holidays_count: Number(e.target.value)})}
              fullWidth
              inputProps={{ min: 0, max: 20 }}
            />

            <TextField
              label="Notas"
              multiline
              rows={3}
              value={formData.notes}
              onChange={(e) => setFormData({...formData, notes: e.target.value})}
              fullWidth
              placeholder="Detalles sobre el cálculo, ajustes especiales..."
            />
          </Box>
        </DialogContent>
        <DialogActions sx={{ p: 3, borderTop: '1px solid #f0f0f0' }}>
          <Button
            onClick={handleCloseDialog}
            variant="outlined"
            sx={{
              backgroundColor: '#FFFFFF',
              color: '#004254',
              borderColor: '#004254',
              textTransform: 'none',
              fontFamily: 'ForFutureSans, sans-serif',
              fontWeight: 700,
              borderRadius: 0,
              px: 3,
              '&:hover': { backgroundColor: '#f0f7f9', borderColor: '#004254' },
            }}
          >
            Cancelar
          </Button>
          <Button
            onClick={handleSubmit}
            variant="contained"
            disabled={!formData.work_center_id || !formData.year}
            sx={{
              backgroundColor: '#004254',
              color: '#FFFFFF',
              textTransform: 'none',
              fontFamily: 'ForFutureSans, sans-serif',
              fontWeight: 700,
              borderRadius: 0,
              px: 3,
              '&:hover': { backgroundColor: '#003040' },
              '&.Mui-disabled': { backgroundColor: '#ccc', color: '#fff' },
            }}
          >
            {editingBag ? 'Actualizar' : 'Crear'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default WorkCenterAnnualBags;